import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Download, FileText, IndianRupee, AlertCircle } from "lucide-react";

const STATUS_COLOR = {
  draft: "text-slate-400", sent: "text-blue-400", accepted: "text-emerald-400",
  converted: "text-purple-400", rejected: "text-red-400", expired: "text-amber-400",
};

export default function PublicQuoteView() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/quotes/${token}`)
      .then((r) => setQ(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Quote not found"));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-gray-900 p-4">
        <div className="glass rounded-lg p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" strokeWidth={2.5} />
          <h1 className="text-2xl font-bold mb-1">Quote Unavailable</h1>
          <p className="text-gray-500 text-sm">{err}</p>
        </div>
      </div>
    );
  }
  if (!q) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-gray-500">Loading…</div>;
  }

  const t = q.totals || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white p-4 md:p-8" data-testid="public-quote-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg f360-gradient flex items-center justify-center shadow-lg">
              <FileText className="w-6 h-6 text-gray-900" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Fire360Pro</div>
              <h1 className="text-xl md:text-2xl font-extrabold">Quotation {q.quote_number}</h1>
            </div>
          </div>
          <Button onClick={() => window.open(`${API}/public/quotes/${token}/pdf`, "_blank")} className="f360-gradient border-0" data-testid="public-pdf-btn">
            <Download className="w-4 h-4 mr-1" strokeWidth={2.5} /> Download PDF
          </Button>
        </div>

        <div className="glass rounded-lg p-6 mb-4">
          <div className="flex justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Quote for</div>
              <div className="text-lg font-bold">{q.customer?.name}</div>
              <div className="text-xs text-gray-500">{q.customer?.address} {q.customer?.city} {q.customer?.pin}</div>
              <div className="text-xs text-gray-500">GSTIN: {q.customer?.gstin || "—"}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Status</div>
              <div className={`text-lg font-bold uppercase ${STATUS_COLOR[q.status]}`}>{q.status}</div>
              <div className="text-xs text-gray-500 mt-1">Valid till: <b className="text-gray-900">{q.valid_until ? new Date(q.valid_until).toLocaleDateString() : "—"}</b></div>
            </div>
          </div>

          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <tr><th className="text-left py-2">Item</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Rate</th><th className="text-right py-2">Amount</th></tr>
              </thead>
              <tbody>
                {q.plan_name && q.plan_amount > 0 && (
                  <tr className="border-b border-gray-200"><td className="py-2">{q.plan_name} Plan ({q.billing_cycle})</td><td className="py-2 text-right">1</td><td className="py-2 text-right">₹ {q.plan_amount.toLocaleString("en-IN")}</td><td className="py-2 text-right">₹ {q.plan_amount.toLocaleString("en-IN")}</td></tr>
                )}
                {(q.modules || []).map((m, i) => (
                  <tr key={`m${i}`} className="border-b border-gray-200"><td className="py-2">{m.name}</td><td className="py-2 text-right">{m.units}</td><td className="py-2 text-right">₹ {m.rate.toLocaleString("en-IN")}</td><td className="py-2 text-right">₹ {m.amount.toLocaleString("en-IN")}</td></tr>
                ))}
                {(q.custom_items || []).map((c, i) => (
                  <tr key={`c${i}`} className="border-b border-gray-200"><td className="py-2">{c.description}</td><td className="py-2 text-right">{c.quantity}</td><td className="py-2 text-right">₹ {c.rate.toLocaleString("en-IN")}</td><td className="py-2 text-right">₹ {c.amount.toLocaleString("en-IN")}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 text-sm">
            <div className="flex justify-between py-0.5"><span className="text-gray-500">Subtotal</span><span>₹ {(t.raw_subtotal || 0).toLocaleString("en-IN")}</span></div>
            {(t.discount_amount || 0) > 0 && (
              <div className="flex justify-between py-0.5 text-amber-300"><span>Discount ({t.discount_pct}%)</span><span>- ₹ {t.discount_amount.toLocaleString("en-IN")}</span></div>
            )}
            <div className="flex justify-between py-0.5"><span className="text-gray-500">GST 18%</span><span>₹ {(t.gst_amount || 0).toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between font-bold text-xl text-orange-300 border-t border-gray-200 pt-2 mt-1">
              <span>Total Payable</span><span><IndianRupee className="w-5 h-5 inline" strokeWidth={2.5} />{(t.total || 0).toLocaleString("en-IN")}</span>
            </div>
          </div>

          {q.notes && <div className="text-xs text-gray-500 italic mt-3">Notes: {q.notes}</div>}

          {q.converted_invoice_number && (
            <div className="mt-3 text-sm bg-purple-500/10 border border-purple-500/30 text-purple-200 rounded-lg p-3">
              This quote has been converted to invoice <b>{q.converted_invoice_number}</b>.
            </div>
          )}
        </div>

        <div className="text-center text-xs text-gray-500">
          Fire360Pro · ACMS SaaS Platform · This is a digital quotation valid till the date specified above.
        </div>
      </div>
    </div>
  );
}
